import { useLocalization } from '@lib/localization';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { pendingDotStyles } from '@pages/AppSettings.styles';
import {
  originStyles, originTextStyles, revertButtonStyles, setHereDotStyles, setHereStyles,
} from '@pages/ReportBranding.styles';
import { UndoAltIcon } from 'react-line-awesome';

/** The layer under the one on screen: the organization's under a team's, else what the layout prints by itself. */
export type BeneathSource = 'organization' | 'builtIn';

interface Props {
  /** Whether the layer on screen sets the field, the unsaved draft included. */
  setHere: boolean;
  /** The field's value is a change that has not been saved yet. */
  pending: boolean;
  beneathSource: BeneathSource;
  /** What the layer beneath holds for the field, in words. Left out where there is nothing to add. */
  beneath?: string;
  /** Takes the field off this layer. Left out where the reader may not change it. */
  onRevert?: () => void;
}

/**
 * The line under a field of a brand: whether this layer sets it, and what the layer beneath
 * prints in its place. A field set here covers that, and is given back to it by the arrow.
 */
const FieldOrigin: React.FC<Props> = ({ setHere, pending, beneathSource, beneath, onRevert }) => {
  const { t } = useLocalization();

  return (
    <Stack direction="row" sx={originStyles}>
      {pending && (
        <Tooltip title={t('Customization_Designer_Unsaved')} arrow>
          <Box sx={pendingDotStyles} />
        </Tooltip>
      )}
      {setHere && <Box sx={setHereDotStyles} />}
      <Box component="span" sx={setHere ? setHereStyles : undefined}>
        {t(setHere ? 'AppSettings_SetHere' : 'ReportBranding_NotSetHere')}
      </Box>
      {beneath && (
        <>
          <Box component="span">·</Box>
          <Box component="span" sx={originTextStyles}>{beneath}</Box>
        </>
      )}
      {setHere && onRevert && (
        <Tooltip
          title={t(beneathSource === 'organization' ? 'ReportBranding_Revert_Organization' : 'ReportBranding_Revert_BuiltIn')}
          arrow
        >
          <IconButton size="small" onClick={onRevert} sx={revertButtonStyles}>
            <UndoAltIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default FieldOrigin;
